/**
 * Template rich text editor using CKEditor 5 (replaces ReactQuill).
 * Same API as before: value, onChange, readOnly, placeholder, ref.insertVariable(variable).
 */

import React, { useRef, useImperativeHandle, forwardRef, useCallback, useState, useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { TemplateClassicEditor, CKEDITOR_LICENSE_KEY } from '../../editor/ckeditor/templateEditorConfig';
import { sanitizeHtml } from './sanitizeHtml';
import { MERGE_TOKEN_DROP_TYPE } from './VariablePanel';
import './DocumentEditor.css';

const DocumentEditor = forwardRef(
  (
    {
      value,
      onChange,
      readOnly = false,
      placeholder = 'Start typing or insert merge variables...',
      enableTable = true,
      onFocus,
      onDropVariable,
      className = '',
    },
    ref
  ) => {
    const editorRef = useRef(null);
    const lastInsertAtRef = useRef(0);
    const lastSentToParentRef = useRef(value ?? '');
    // Uncontrolled pattern: remount editor only when content is loaded from outside.
    // Avoids cursor jumping to start when typing (caused by data prop triggering setData).
    const [loadKey, setLoadKey] = useState(0);
    const initialDataRef = useRef(value ?? '');

    useEffect(() => {
      if (value !== lastSentToParentRef.current) {
        lastSentToParentRef.current = value ?? '';
        initialDataRef.current = value ?? '';
        setLoadKey((k) => k + 1);
      }
    }, [value]);

    useImperativeHandle(
      ref,
      () => ({
        insertVariable: (variable) => {
          const editor = editorRef.current;
          if (!editor) return;
          const now = Date.now();
          if (now - lastInsertAtRef.current < 250) return;
          lastInsertAtRef.current = now;
          // Focus the editor first so selection is valid (clicking sidebar loses focus)
          editor.editing.view.focus();
          editor.execute('insertMergeToken', {
            key: variable.key,
            token: variable.token,
            label: variable.label,
            unique: variable.unique,
          });
        },
        getEditor: () => editorRef.current,
      }),
      []
    );

    const handleReady = useCallback((editor) => {
      editorRef.current = editor;
    }, []);

    const handleChange = useCallback(
      (_event, editor) => {
        const data = editor.getData();
        const sanitized = sanitizeHtml(data);
        lastSentToParentRef.current = sanitized;
        if (onChange) onChange(sanitized);
      },
      [onChange]
    );

    const handleFocus = useCallback(
      (evt, editor) => {
        window.dispatchEvent(
          new CustomEvent('ckeditor-focus', { detail: { editor, editorRef } })
        );
        if (onFocus) onFocus(evt, editor);
      },
      [onFocus]
    );

    const handleDragOver = useCallback((e) => {
      if (e.dataTransfer.types.includes(MERGE_TOKEN_DROP_TYPE)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    }, []);

    const handleDrop = useCallback(
      (e) => {
        const json = e.dataTransfer.getData(MERGE_TOKEN_DROP_TYPE);
        if (!json || !onDropVariable) return;
        e.preventDefault();
        e.stopPropagation();
        try {
          const variable = JSON.parse(json);
          onDropVariable(variable);
        } catch {
          // ignore parse errors
        }
      },
      [onDropVariable]
    );

    // Toolbar and most options come from TemplateClassicEditor.defaultConfig (sample-aligned).
    // Only override placeholder, initialData, and optionally table.
    const config = React.useMemo(
      () => ({
        licenseKey: CKEDITOR_LICENSE_KEY,
        placeholder,
        initialData: initialDataRef.current,
        link: { defaultProtocol: 'https://' },
        ...(enableTable
          ? {}
          : {
              toolbar: {
                items: [
                  'undo', 'redo', '|', 'showBlocks', 'findAndReplace', 'textPartLanguage', 'fullscreen', '|',
                  'heading', '|', 'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
                  'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', 'code', 'removeFormat', '|',
                  'specialCharacters', 'horizontalLine', 'pageBreak', 'link', 'insertImage', 'insertImageViaUrl', 'mediaEmbed',
                  'highlight', 'blockQuote', 'codeBlock', '|', 'alignment', '|',
                  'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent',
                ],
                shouldNotGroupWhenFull: false,
              },
            }),
      }),
      // loadKey ensures config.initialData is refreshed when content is loaded from outside
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [placeholder, enableTable, loadKey]
    );

    return (
      <div
        className={`document-editor-wrapper ${className}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="editor-shell">
          <CKEditor
            key={loadKey}
            editor={TemplateClassicEditor}
            onChange={handleChange}
            onReady={handleReady}
            onFocus={handleFocus}
            config={config}
            disabled={readOnly}
          />
        </div>
      </div>
    );
  }
);

DocumentEditor.displayName = 'DocumentEditor';

export default DocumentEditor;
